import PropTypes from 'prop-types';

/**
 * Parses a cost value into a number, stripping out any comma separators, converting
 * null and undefined to 0.
 *
 * @param {string|number} mCost -The raw cost input, which may be a
 *  number or a string containing commas (e.g. "1,234.56").
 * @returns {number} - The parsed numeric value
 */
export const fnParseCost = (mCost) => {
  const sCost = String(mCost ?? 0);
  return parseFloat(sCost.replace(/,/g, '')) || 0;
};

/**
 * Calculates the total event cost by summing each guest's registration cost,
 * minus any previously paid amount.
 *
 * @param {object} oEventCartItem - Event from the Redux store's cart.
 * @returns {number} - The total cost of the event.
 */
export const fnCalculateEventCost = (oEventCartItem) => {
  const { aGuests } = oEventCartItem.oDetails;
  const oHost = aGuests.find((oGuest) => oGuest.ROLE === 'currentHost');
  const nHostTotalPaid = oHost ? fnParseCost(oHost.TOTAL_AMOUNT_PAID ?? 0) : 0;

  const nEventCost = aGuests.reduce((nTotal, oGuest) => {
    const nCurrentCost = fnParseCost(oGuest.REG_OPTION?.COST);
    return nTotal + nCurrentCost;
  }, 0);

  // Subtract the host's total paid from the event cost
  return Math.max(nEventCost - nHostTotalPaid, 0); // Ensure the cost is not negative
};

/**
 * Prop type definition for event cart item.
 */
export const eventCartItemPropType = PropTypes.shape({
  sId: PropTypes.string.isRequired,
  oDetails: PropTypes.shape({
    oEvent: PropTypes.shape({
      TITLE: PropTypes.string.isRequired,
      REGISTRATION: PropTypes.shape({
        REGISTRATION_OPTIONS: PropTypes.arrayOf(
          PropTypes.shape({
            COST: PropTypes.number.isRequired,
          })
        ).isRequired,
      }).isRequired,
    }).isRequired,
    aGuests: PropTypes.arrayOf(
      PropTypes.shape({
        REG_OPTION: PropTypes.shape({
          COST: PropTypes.number.isRequired,
        }),
      })
    ).isRequired,
  }).isRequired,
});

/**
 * Calculates the maximum number of registrants allowed to register for an event.
 * Essentially, it's the max number of guests plus the registrant, unless the event capacity is reached.
 *
 * @param {number} iEventCapacity - Event capacity
 * @param {number} iTotalRegistrations - Total number of registrations
 * @param {number} iMaxGuests - Maximum number of guests allowed per registrant
 * @returns {number} - The maximum number of registrants allowed for the event.
 */
export const fnCalculateMaxNumberOfRegistrantsForEvent = (
  iEventCapacity,
  iTotalRegistrations,
  iMaxGuests
) => {
  if (iMaxGuests === 0) {
    return 1;
  }

  const iMaxGuestsPlusRegistrant = iMaxGuests + 1;
  if (iEventCapacity === 0) {
    return iMaxGuestsPlusRegistrant;
  }

  const iRemainingCapacity = iEventCapacity - iTotalRegistrations;
  if (iRemainingCapacity < iMaxGuestsPlusRegistrant) {
    return iRemainingCapacity;
  }
  return iMaxGuestsPlusRegistrant;
};

/**
 * Formats the event registration confirmation email data.
 *
 * @param {object} oCheckout - Redux store Checkout state.
 * @param {array} aEventCartItems - Array of event cart items.
 * @returns {object} - The formatted event registration confirmation email data.
 */
export const fnFormatEventRegConfirmationEmailData = (
  oCheckout,
  aEventCartItems
) => {
  let oResult = {};
  const { email, firstName, lastName, userLookupId } = oCheckout;
  const oUserInformation = {
    email,
    firstName,
    lastName,
    lookupId: userLookupId,
  };
  const oFirstEventInCartDetails = aEventCartItems[0].oDetails.oEvent;
  const sConfirmationMessage =
    oFirstEventInCartDetails.REGISTRATION.CONFIRMATION_MESSAGE;
  // Use the main event contact if the user registered for a sub-event
  const oEventContact =
    oFirstEventInCartDetails.MAIN_EVENT?.CONTACT ??
    oFirstEventInCartDetails.CONTACT;

  oResult = {
    oUserInformation,
    sConfirmationMessage,
    oEventContact,
  };
  return oResult;
};
